/**
 * RankedContractDetail: the ranked table's expanded row (entry / surface score drivers, ML telemetry), shown only
 * with Full Analytics on. Moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { AnalyticsMetric, badgeStyle, mutedText, secondaryText, ui } from "@components/screener/screenerUi";
import { numberFmt, pctFmt, titleizeBucket } from "@lib/screenerTruth";
import { contributionTone, toneFromTenorBucket } from "@lib/screenerViewHelpers";
import { type Purchase, type SurfaceDriverBreakdown } from "@lib/screenerTypes";

export type RankedContractDetailProps = {
  columnCount: number;
  entryContribs: { vega?: number | null | undefined; ml?: number | null | undefined; surface?: number | null | undefined; } | null;
  entryRaw: { vega_norm?: number | null | undefined; ml_score?: number | null | undefined; surface_norm?: number | null | undefined; } | null;
  entryWeights: { vega?: number | null | undefined; ml?: number | null | undefined; surface?: number | null | undefined; } | null;
  p: Purchase;
  surfaceContext: { atm_iv?: number | null | undefined; iv_spread?: number | null | undefined; market_term_key?: string | null | undefined; market_term_percentile?: number | null | undefined; market_surface_trade_date?: string | null | undefined; policy_preferred_bucket?: string | null | undefined; market_best_bucket?: string | null | undefined; final_preferred_bucket?: string | null | undefined; } | null;
  surfaceDrivers: SurfaceDriverBreakdown | null;
  surfaceScores: { tenor?: number | null | undefined; value?: number | null | undefined; shape?: number | null | undefined; } | null;
};

export default function RankedContractDetail({
  columnCount,
  entryContribs,
  entryRaw,
  entryWeights,
  p,
  surfaceContext,
  surfaceDrivers,
  surfaceScores,
}: RankedContractDetailProps) {
  return (
    <tr
      style={{
        backgroundColor: "rgba(15, 23, 42, 0.04)",
        borderTop: `1px solid ${ui.cardBorder}`,
      }}
    >
      <td className="px-4 py-4" colSpan={columnCount}>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          {/* "PRICE & ALLOCATION CONTEXT" panel removed 2026-08-11.
              Every metric it showed is already a column on this row —
              bid, ask, unit cost, OI, liquidity tier, max-initial qty
              and the Context/Qty cells — and the drawer can only be
              opened while those columns are on screen, since the
              Open button lives inside the same showAnalyticsColumns
              block. The sizing-cap fields that were NOT duplicated
              moved to the cell tooltips above. */}

          <div
            className="rounded-3xl border p-4 xl:col-span-1"
            style={{ backgroundColor: ui.cardBg, borderColor: ui.cardBorder }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Entry score drivers</div>
                <div className="mt-1 text-xs" style={mutedText}>
                  Weighted blend of vega quality and surface quality. The ML term is retained at zero weight and contributes nothing to the rank — its weight and contribution are shown in the row below.
                </div>
              </div>
              {/* Run-level deployment-mode badge removed 2026-08-11 —
                  it is not a per-row fact and already sits on the
                  Snapshot card. */}
            </div>

            {/* "Vega norm" tile removed 2026-08-11 — it restated
                entryRaw.vega_norm, which is the "Vega raw" figure in
                the driver row directly below, and contract vega is a
                table column. The Entry score tile STAYS: it is the
                total the three driver contributions must sum to. The
                ML percentile tile STAYS: mlModelStatus is the only
                per-row signal that the model fell back rather than
                scored. */}
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <AnalyticsMetric
                label="Entry score"
                value={numberFmt(p.entryScore, 3)}
                subtext={`Surface ${numberFmt(p.surfaceScoreNorm, 3)} · ML ${numberFmt(p.mlScore, 3)}`}
              />
              <AnalyticsMetric
                label="ML percentile"
                value={pctFmt(p.mlPercentile, 0)}
                subtext={p.mlModelVersion ? `Model ${p.mlModelVersion}` : p.mlModelStatus || "Telemetry pending"}
              />
            </div>

            <div className="mt-4 space-y-3">
              {[
                {
                  key: "vega",
                  label: "Vega",
                  raw: entryRaw?.vega_norm,
                  weight: entryWeights?.vega,
                  contribution: entryContribs?.vega,
                },
                {
                  key: "ml",
                  label: "ML",
                  raw: entryRaw?.ml_score,
                  weight: entryWeights?.ml,
                  contribution: entryContribs?.ml,
                },
                {
                  key: "surface",
                  label: "Surface",
                  raw: entryRaw?.surface_norm,
                  weight: entryWeights?.surface,
                  contribution: entryContribs?.surface,
                },
              ].map((item) => (
                <div key={item.key}>
                  <div className="flex items-center justify-between gap-3 text-xs">
                    <span style={secondaryText}>
                      {item.label} raw {numberFmt(item.raw ?? null, 3)} × {numberFmt(item.weight ?? null, 2)}
                    </span>
                    <span style={badgeStyle(contributionTone(item.contribution))}>
                      {numberFmt(item.contribution ?? null, 3)}
                    </span>
                  </div>
                  {/* Contribution progress bar removed 2026-08-11 —
                      the numeric "raw × weight = contribution" line
                      above is the auditable form, and it is what must
                      sum to the entry score. The bar only re-drew it. */}
                </div>
              ))}
            </div>
          </div>

          <div
            className="rounded-3xl border p-4 xl:col-span-1"
            style={{ backgroundColor: ui.cardBg, borderColor: ui.cardBorder }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Surface score drivers</div>
                <div className="mt-1 text-xs" style={mutedText}>
                  Tenor preference, value label, and shape label behind the surface score.
                </div>
              </div>
              <span style={badgeStyle(toneFromTenorBucket(p.tenorBucket))}>
                {titleizeBucket(p.tenorBucket)}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <AnalyticsMetric
                label="Surface score"
                value={numberFmt(p.surfaceScore, 3)}
                subtext={`Raw ${numberFmt(p.surfaceScoreRaw, 3)} · Norm ${numberFmt(p.surfaceScoreNorm, 3)}`}
              />
              <AnalyticsMetric
                label="Value label"
                value={surfaceDrivers?.labels?.absolute_value || p.surfaceAbsoluteValueLabel || surfaceDrivers?.labels?.value || p.surfaceValueLabel || "—"}
                subtext={
                  surfaceContext?.iv_spread != null || p.surfaceIvSpread != null
                    ? `Relative ${surfaceDrivers?.labels?.relative_value || p.surfaceRelativeValueLabel || "—"} · IV spread ${pctFmt(surfaceContext?.iv_spread ?? p.surfaceIvSpread ?? null, 2)} vs ATM`
                    : "No spread telemetry"
                }
              />
              <AnalyticsMetric
                label="Shape label"
                value={surfaceDrivers?.labels?.shape || p.surfaceShapeLabel || "—"}
                subtext={surfaceDrivers?.labels?.regime || p.surfaceRegime || "—"}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <AnalyticsMetric
                label="Tenor score"
                value={numberFmt(surfaceScores?.tenor ?? p.tenorOpportunityScore ?? null, 3)}
                subtext={`Final bucket ${titleizeBucket(surfaceContext?.final_preferred_bucket || p.tenorBucket)}`}
              />
              <AnalyticsMetric
                label="Value score"
                value={numberFmt(surfaceScores?.value ?? null, 3)}
                subtext={surfaceContext?.market_term_key ? `Term key ${surfaceContext.market_term_key}` : "Term map pending"}
              />
              <AnalyticsMetric
                label="Shape score"
                value={numberFmt(surfaceScores?.shape ?? null, 3)}
                subtext={
                  surfaceContext?.market_term_percentile != null
                    ? `Percentile ${pctFmt(surfaceContext.market_term_percentile, 0)}`
                    : "Percentile pending"
                }
              />
            </div>

            <div className="mt-4 rounded-2xl border p-3" style={{ borderColor: ui.cardBorder }}>
              <div className="text-xs font-semibold" style={secondaryText}>
                Surface context
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
                <div style={secondaryText}>
                  ATM IV: <span style={{ color: ui.textPrimary }}>{pctFmt(surfaceContext?.atm_iv ?? null, 2)}</span>
                </div>
                <div style={secondaryText}>
                  Market best bucket: <span style={{ color: ui.textPrimary }}>{titleizeBucket(surfaceContext?.market_best_bucket || "")}</span>
                </div>
                <div style={secondaryText}>
                  Policy preferred: <span style={{ color: ui.textPrimary }}>{titleizeBucket(surfaceContext?.policy_preferred_bucket || "")}</span>
                </div>
                <div style={secondaryText}>
                  Surface trade date: <span style={{ color: ui.textPrimary }}>{surfaceContext?.market_surface_trade_date || "—"}</span>
                </div>
              </div>
            </div>
          </div>

          <div
            className="rounded-3xl border p-4 xl:col-span-1"
            style={{ backgroundColor: ui.cardBg, borderColor: ui.cardBorder }}
          >
            <div className="text-sm font-semibold">ML telemetry</div>
            <div className="mt-1 text-xs" style={mutedText}>
              Entry-model telemetry, shown as context only. The ML term carries zero weight in all four deployment modes, so it does not tilt the rank. The score below indicates whether the model scored this row or fell back.
            </div>
            {/* Driver summary tile and the top positive/negative
                driver chips removed 2026-08-11. ML weight has been
                0.0 in all four deployment modes since 2026-08-07, so
                feature attribution explains a term that no longer
                moves the rank. The ML score tile STAYS — mlModelStatus
                on it is the per-row signal that the model fell back
                rather than scored. All fields remain in the payload
                and the printable export. */}
            <div className="mt-4 grid grid-cols-1 gap-3">
              <AnalyticsMetric
                label="ML score"
                value={numberFmt(p.mlScore, 3)}
                subtext={p.mlConfidence || p.mlBucket || p.mlModelStatus || "Model telemetry"}
              />
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}
